"use client";

import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { m } from "framer-motion";
import Image from "next/image";
import React from "react";
import { AppStoreButtons } from "./app-store-buttons";

export const AppDownload: React.FC = () => {
	const { ref: contentRef, isInView: contentInView } = useScrollAnimation<HTMLDivElement>({
		threshold: 0.3,
		triggerOnce: true,
	});

	return (
		<section className="relative min-h-[650px] md:min-h-[700px] lg:min-h-[800px] bg-gradient-to-br from-primary via-primary/90 to-primary/80">
			{/* Main Phone Image with Simplified Animations */}
			<div className="absolute top-[-7%] right-0 md:right-[-50px] lg:right-0 z-10 flex items-center justify-end w-full">
				<Image
					src="/images/home/app-download.webp"
					alt="Settlin mobile app interface showing property listings and features"
					width={1200}
					height={600}
					className="w-full h-auto max-w-[60%]"
					style={{ animation: "float 6s ease-in-out infinite" }}
				/>
			</div>

			{/* Simplified Background Pattern */}
			<div className="absolute inset-0 opacity-10">
				<div className="absolute top-10 left-10 w-20 h-20 border-2 border-white rounded-full animate-pulse"></div>
				<div className="absolute top-32 right-20 w-16 h-16 border-2 border-white rounded-full animate-pulse"></div>
				<div className="absolute bottom-20 left-20 w-12 h-12 border-2 border-white rounded-full animate-pulse"></div>
				<div className="absolute bottom-32 right-10 w-8 h-8 border-2 border-white rounded-full animate-pulse"></div>
			</div>

			{/* Phone Mockups Container - Fixed positioning to prevent overflow */}
			<div className="absolute right-0 top-[40px] md:top-[60px] w-[280px] sm:w-[350px] md:w-[450px] lg:w-[550px] h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px] z-10 max-w-[45%] md:max-w-[50%] lg:max-w-none">
				<div className="relative w-full h-full">
					{/* Simplified Floating Elements */}
					<div className="absolute top-[10%] left-[10%] w-8 h-8 bg-white/20 rounded-full backdrop-blur-sm animate-pulse"></div>
					<div className="absolute top-[20%] right-[15%] w-6 h-6 bg-white/15 rounded-full backdrop-blur-sm animate-pulse"></div>
					<div className="absolute bottom-[30%] left-[5%] w-10 h-10 bg-white/10 rounded-full backdrop-blur-sm animate-pulse"></div>
					<div className="absolute bottom-[15%] right-[20%] w-4 h-4 bg-white/25 rounded-full animate-pulse"></div>
				</div>
			</div>

			{/* Content Container */}
			<div className="container relative z-20 py-8 md:py-12 lg:py-16 h-full flex items-center">
				<div
					ref={contentRef}
					className="max-w-full md:max-w-[500px] lg:max-w-[600px] px-4 md:px-6 lg:px-8 w-full lg:w-1/2"
				>
					{/* Badge */}
					<div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 mb-6">
						<div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
						<span className="text-white text-sm font-medium">Available Now</span>
					</div>

					{/* Main Heading */}
					<m.h2
						className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 md:mb-6"
						initial={{ opacity: 0, y: 25 }}
						animate={contentInView ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.4, ease: "easeOut", delay: 0.05, type: "tween" }}
						style={{ willChange: contentInView ? "transform, opacity" : "auto" }}
					>
						Download our
						<br />
						<span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">mobile app</span>
					</m.h2>

					{/* Subheading */}
					<p className="text-white/90 text-base md:text-lg lg:text-xl mb-8 md:mb-10 leading-relaxed max-w-[450px]">
						Get the Settlin app for seamless property browsing, instant notifications, and exclusive deals right at your
						fingertips.
					</p>

					{/* Features List */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 md:mb-10">
						{["🏠 Browse properties anywhere", "🔔 Real-time notifications", "📱 Request for a property visit"].map(
							feature => {
								return (
									<div key={feature} className="flex items-center gap-2">
										<span className="text-lg">{feature.split(" ")[0]}</span>
										<span className="text-white/90 text-sm md:text-base">{feature.split(" ").slice(1).join(" ")}</span>
									</div>
								);
							},
						)}
					</div>

					{/* Store Download Buttons */}
					<AppStoreButtons />

					{/* Additional Info */}
					<div className="flex items-center gap-4 mt-6 text-white/70 text-sm" style={{ animationDelay: "0.6s" }}>
						<div className="flex items-center gap-1">
							<span className="text-yellow-400">★★★★★</span>
							<span>4.6/5 rating</span>
						</div>
						<div className="w-px h-4 bg-white/30"></div>
						<span>10k+ downloads</span>
					</div>
				</div>
			</div>
		</section>
	);
};
